import { curry, isFunction } from "./function"

/*
**  Function to test if an object is a plain object, i.e. is constructed
**  by the built-in Object constructor and inherits directly from Object.prototype
**  or null. Some built-in objects pass the test, e.g. Math which is a plain object
**  and some host or exotic objects may pass also.
**  Modified from http://stackoverflow.com/questions/5876332/how-can-i-differentiate-between-an-object-literal-other-javascript-objects
**  See also http://stackoverflow.com/questions/10919915/ie8-getprototypeof-method
**
**  @param {} obj - value to test
**  @returns {Boolean} true if passes tests, false otherwise
*/
export const isPlainObject = (obj) => {
  // Basic check for Type object that's not null
  if (typeof obj === "object" && obj !== null) {
    // If Object.getPrototypeOf supported, use it
    if (typeof Object.getPrototypeOf === "function") {
      const proto = Object.getPrototypeOf(obj)
      return proto === Object.prototype || proto === null
    }

    // Otherwise, use internal class
    // This should be reliable as if getPrototypeOf not supported, is pre-ES5
    return Object.prototype.toString.call(obj) === "[object Object]"
  }

  // Not an object
  return false
}

export const mapKeys = curry((fn, obj) =>
  Object.keys(obj).reduce((result, key) => {
    result[fn(key, obj[key])] = obj[key]
    return result
  }, {}))

export const mapValues = curry((fn, obj) =>
  Object.keys(obj).reduce((result, key) => {
    result[key] = fn(obj[key], key)
    return result
  }, {}))

export const mapKeysDeep = fn => (obj) => {
  if (!isPlainObject(obj) && !Array.isArray(obj)) return obj
  const mapper = mapKeysDeep(fn)
  return Object.keys(obj).reduce((result, key) => {
    const val = obj[key]
    let mappedVal = val
    if (Array.isArray(val)) mappedVal = val.map(mapper)
    else if (isPlainObject(val)) mappedVal = mapper(val)
    result[fn(key)] = mappedVal
    return result
  }, {})
}

export const get = curry((pathSpec, obj) => {
  const path = typeof pathSpec === "string" ? pathSpec.split(".") : pathSpec

  if (!Array.isArray(path)) {
    console.warn("Expecting path to be a string or an array of strings")
    return undefined
  }

  const shouldPick = found => typeof found === "object" && !Array.isArray(found) && found !== null
  const [first, ...rest] = path

  return rest.reduce((found, key) => (shouldPick(found) ? found[key] : found), obj[first])
})

export const pick = curry((keys, obj) =>
  keys.reduce((o, key) => {
    // Sometime we need to pick from special objects like Window, which in IE does not register
    // as a plain object
    if (!isPlainObject(obj) && typeof obj === "object") o[key] = obj[key]
    else if (obj.hasOwnProperty(key)) o[key] = obj[key]
    return o
  }, {}))

export const omit = curry((keys, obj) =>
  Object.keys(obj).reduce((o, key) => {
    if (!~keys.indexOf(key)) o[key] = obj[key]
    return o
  }, {}))

export const toPairs = (obj) => {
  const isArray = Array.isArray(obj)

  return Object.keys(obj).reduce((pairs, key) => {
    pairs.push([isArray ? parseInt(key, 10) : key, obj[key]])
    return pairs
  }, [])
}

export const shallowEq = curry((obj1, obj2) => {
  const [keys1, keys2] = [obj1, obj2].map(Object.keys)

  if (keys1.length !== keys2.length) return false
  if (keys1.some(key => keys2.indexOf(key) === -1)) return false

  return keys1.every((key) => {
    const [v1, v2] = [obj1[key], obj2[key]]
    return isNaN(v1) && isNaN(v1) ? true : v1 === v2
  })
})

export const isEmpty = obj => (isPlainObject(obj) ? !Object.keys(obj).length : true)

// !!Mutations be here, affects original object
export const bindAll = curry((attrs, context) => {
  (Array.isArray(attrs) && attrs.length ? attrs : Object.keys(context))
    .filter(key => isFunction(context[key]))
    .forEach((key) => {
      context[key] = context[key].bind(context)
    })
})



// WEBPACK FOOTER //
// ./src/utils/object.js